import { AxiosError } from "axios";
import { usePostHog } from "posthog-js/react";
import { useCallback, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { HiddenWhenSpiffOnly } from "@/core/components/hidden-when-spiff-only";
import { TimeframeValue } from "@/core/components/time-frame-filter";
import {
  SimpleSidebarLayout,
  SimpleSidebarContent,
} from "@/core/components/ui/simple-sidebar";
import { Stack } from "@/core/components/ui/stack";
import { toast } from "@/core/components/ui/toast";
import { Typography } from "@/core/components/ui/typography";
import { useGetBusiness } from "@/business/query";
import { useBusiness } from "@/core/business";
import { dayjs } from "@/core/utils/date";
import { multiFieldSearch } from "@/core/utils/multi-field-search";
import { useViewPreferences } from "@/core/view-preferences/view-preferences-context";
import AddAdvocateModal from "@/customer/common/add-advocate-modal";
import { useGetMyUser } from "@/user/query";
import AddReferralModal from "./common/add-referral-modal";
import { PayoutModal } from "./common/referral-payout-modal";
import { REFERRAL_COLUMN_TO_STATUS } from "./constants";
import { trackReferralUpdate } from "./events";
import { NoReferrals } from "./no-referrals";
import {
  useGetActiveReferrals,
  useDeleteReferral,
  useUpdateReferral,
} from "./query";
import type {
  IReferral,
  ReferralColumn,
  ReferralUpdateParams,
} from "./referral";
import ReferralKanbanView from "./referral-kanban-view";
import { UpdateReferralInfoModal } from "./referral-kanban-view/update-referral-info-modal";
import ReferralTableView from "./referral-table-view";
import { ReferralsHeader } from "./referrals-header";
import { ReferralsSidebar } from "./referrals-sidebar";
import { REFERRAL_SEARCH_FIELDS } from "./search-constants";

export interface UpdateInfoModal {
  referral: IReferral | null;
  targetColumn: ReferralColumn | null;
}

const ReferralsContent = () => {
  const posthog = usePostHog();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const selected = searchParams.get("selected");
  const { referralView, setReferralView } = useViewPreferences();
  const { businessId } = useBusiness();
  const { data: user } = useGetMyUser();

  const { data: businessDetails } = useGetBusiness(businessId);
  const { data: referrals = [], isLoading: isLoadingReferrals } =
    useGetActiveReferrals(businessId);

  const { mutate: updateReferral } = useUpdateReferral(businessId);
  const { mutate: deleteReferral } = useDeleteReferral(businessId);

  const [searchValue, setSearchValue] = useState("");
  const [timeframeValue, setTimeframeValue] =
    useState<TimeframeValue>("all_time");
  const [payoutReferral, setPayoutReferral] = useState<IReferral | null>(null);
  const [isAddReferralModalOpen, setIsAddReferralModalOpen] = useState(false);
  const [isAddAdvocateModalOpen, setIsAddAdvocateModalOpen] = useState(false);
  const [updateInfoModal, setUpdateInfoModal] =
    useState<UpdateInfoModal | null>(null);

  const isServiceTitanEnabled = businessDetails?.service_titan_enabled || false;
  const effectiveView =
    referralView || (isServiceTitanEnabled ? "table" : "kanban");

  // Filter referrals based on search and timeframe
  const filteredReferrals = useMemo(() => {
    let filtered = referrals;

    // Apply search filter using multi-field search
    if (searchValue) {
      filtered = multiFieldSearch(
        filtered,
        searchValue,
        REFERRAL_SEARCH_FIELDS,
      );
    }

    // Apply timeframe filter
    if (timeframeValue === "last_thirty_days") {
      filtered = filtered.filter((referral) =>
        dayjs(referral.created_at).isAfter(dayjs().subtract(30, "day")),
      );
    }

    return filtered;
  }, [referrals, searchValue, timeframeValue]);

  // Get the selected referral from the query param
  const selectedReferral = useMemo(
    () => (selected ? referrals.find((r) => r.id === selected) || null : null),
    [selected, referrals],
  );

  const handleSidebarClose = useCallback(() => {
    navigate("/referrals", { replace: true });
  }, [navigate]);

  const handleReferralUpdate = useCallback(
    ({
      referral,
      updates,
      method,
      onSuccessCallback,
    }: {
      referral: IReferral;
      updates: Partial<ReferralUpdateParams>;
      method: "sidebar" | "modal" | "drag_and_drop";
      onSuccessCallback?: (data: IReferral) => void;
    }) => {
      updateReferral(
        { id: referral.id, updates },
        {
          onSuccess: (data) => {
            if (updates.status === "LOST") {
              if (selectedReferral?.id === referral.id) {
                handleSidebarClose();
              }
              toast.success("Referral marked as lost");
            } else {
              toast.success("Referral status updated successfully");
            }
            trackReferralUpdate(posthog, {
              referral,
              updates,
              method,
              success: true,
            });
            if (selectedReferral?.id === data.id) {
              navigate(`/referrals?selected=${data.id}`, { replace: true });
            }
            onSuccessCallback?.(data);
          },
          onError: (error) => {
            trackReferralUpdate(posthog, {
              referral,
              updates,
              method,
              success: false,
              error,
            });
            let title = "Failed to update referral status";
            if (error instanceof AxiosError) {
              title = Array.isArray(error.response?.data?.error) ? (
                <Stack gap="sm">
                  {error.response?.data?.error.map((e: string) => (
                    <Typography
                      variant="captionW1"
                      textColor="primaryInverse"
                      key={e}
                    >
                      {e}
                    </Typography>
                  ))}
                </Stack>
              ) : (
                error.response?.data?.error || title
              );
            }
            toast.error(title);
          },
        },
      );
    },
    [updateReferral, selectedReferral, posthog, navigate, handleSidebarClose],
  );

  const handleUpdateReferral = useCallback(
    (values: Partial<ReferralUpdateParams>, referral: IReferral) => {
      handleReferralUpdate({
        referral,
        updates: values,
        method: "sidebar",
      });
    },
    [handleReferralUpdate],
  );

  const handleUpdateDraggedReferral = useCallback(
    (values: Partial<ReferralUpdateParams>) => {
      if (!updateInfoModal?.referral || !updateInfoModal?.targetColumn) return;
      const updates = {
        status: REFERRAL_COLUMN_TO_STATUS[updateInfoModal.targetColumn],
        ...values,
      };
      handleReferralUpdate({
        referral: updateInfoModal.referral,
        updates,
        method: "modal",
        onSuccessCallback: () => setUpdateInfoModal(null),
      });
    },
    [updateInfoModal, handleReferralUpdate],
  );

  const handleSidebarUpdate = useCallback(
    (values: Partial<ReferralUpdateParams>) => {
      if (!selectedReferral) return;
      handleUpdateReferral(values, selectedReferral);
    },
    [selectedReferral, handleUpdateReferral],
  );

  const handleDeleteReferral = useCallback(
    (referral: IReferral) => {
      deleteReferral(
        { id: referral.id },
        {
          onSuccess: () => {
            if (selectedReferral?.id === referral.id) {
              handleSidebarClose();
            }
            toast.success("Referral deleted successfully");
          },
          onError: () => {
            toast.error("Failed to delete referral");
          },
        },
      );
    },
    [deleteReferral, selectedReferral, handleSidebarClose],
  );

  const handleFinalPayout = useCallback(
    (referralId: string) => {
      const currentSelected = searchParams.get("selected");
      if (currentSelected === referralId) {
        handleSidebarClose();
      }
    },
    [searchParams, handleSidebarClose],
  );

  if (!user) return null;

  return (
    <SimpleSidebarLayout
      open={!!selectedReferral}
      onOpenChange={(open) => !open && handleSidebarClose()}
      data-cy="referrals-view"
    >
      <SimpleSidebarContent removeScrollArea={effectiveView === "table"}>
        <ReferralsHeader
          referralsCount={filteredReferrals.length}
          isLoadingReferrals={isLoadingReferrals}
          searchValue={searchValue}
          onSearchChange={setSearchValue}
          timeframeValue={timeframeValue}
          onTimeframeChange={setTimeframeValue}
          viewType={effectiveView}
          onViewChange={(view) => setReferralView(view)}
          onAddAdvocate={() => setIsAddAdvocateModalOpen(true)}
          onAddReferral={() => setIsAddReferralModalOpen(true)}
          noReferrals={!referrals.length && !isLoadingReferrals}
        />

        {!referrals.length && !isLoadingReferrals ? (
          <NoReferrals />
        ) : (
          <>
            {effectiveView === "table" ? (
              <ReferralTableView
                referrals={filteredReferrals}
                isLoadingReferrals={isLoadingReferrals}
                selectedReferral={selectedReferral}
                onUpdateReferral={handleUpdateReferral}
                onDeleteReferral={handleDeleteReferral}
                onRowClick={(referral) => {
                  if (selectedReferral?.id === referral.id) {
                    navigate("/referrals", { replace: true });
                  } else {
                    navigate(`/referrals?selected=${referral.id}`, {
                      replace: true,
                    });
                  }
                }}
                onPayout={setPayoutReferral}
              />
            ) : (
              <ReferralKanbanView
                referrals={filteredReferrals}
                isLoadingReferrals={isLoadingReferrals}
                selectedReferral={selectedReferral}
                setUpdateInfoModal={setUpdateInfoModal}
                updateInfoModal={updateInfoModal}
                onUpdateReferral={handleUpdateReferral}
                onDeleteReferral={handleDeleteReferral}
                onCardClick={(referral) => {
                  posthog?.capture("referral_card_clicked", {
                    referral_id: referral.id,
                    current_status: referral.status,
                    is_deselecting: selected === referral.id,
                  });

                  if (selected === referral.id) {
                    navigate("/referrals", { replace: true });
                  } else {
                    navigate(`/referrals?selected=${referral.id}`, {
                      replace: true,
                    });
                  }
                }}
                onPayout={setPayoutReferral}
              />
            )}
          </>
        )}
      </SimpleSidebarContent>

      <ReferralsSidebar
        referral={selectedReferral}
        viewType={effectiveView}
        onClose={handleSidebarClose}
        onUpdate={handleSidebarUpdate}
        onNextColumn={(column, referral) => {
          setUpdateInfoModal({
            referral,
            targetColumn: column,
          });
        }}
        onLost={() => {
          if (!selectedReferral) return;
          handleUpdateReferral({ status: "LOST" }, selectedReferral);
        }}
        onPayout={() => {
          if (!selectedReferral) return;
          setPayoutReferral(selectedReferral);
        }}
        onDelete={() => {
          if (!selectedReferral) return;
          handleDeleteReferral(selectedReferral);
        }}
      />

      <AddAdvocateModal
        open={isAddAdvocateModalOpen}
        onOpenChange={setIsAddAdvocateModalOpen}
        onSuccess={() => {
          setIsAddAdvocateModalOpen(false);
        }}
      />
      <AddReferralModal
        open={isAddReferralModalOpen}
        onOpenChange={setIsAddReferralModalOpen}
        onSuccess={() => {
          setIsAddReferralModalOpen(false);
        }}
      />
      <PayoutModal
        referral={payoutReferral}
        onOpenChange={setPayoutReferral}
        onFinalPayout={handleFinalPayout}
      />

      <UpdateReferralInfoModal
        open={!!updateInfoModal}
        onOpenChange={(open) =>
          setUpdateInfoModal(open ? updateInfoModal : null)
        }
        referral={updateInfoModal?.referral}
        targetColumn={updateInfoModal?.targetColumn}
        onSubmit={handleUpdateDraggedReferral}
      />
    </SimpleSidebarLayout>
  );
};

const Referrals = () => (
  <HiddenWhenSpiffOnly>
    <ReferralsContent />
  </HiddenWhenSpiffOnly>
);

export default Referrals;
